import {
  ref,
  onValue,
  off,
  query,
  orderByChild,
  limitToLast,
  serverTimestamp,
  startAt,
  endAt,
  equalTo,
  increment,
} from 'firebase/database'
import db from 'domains/db'
import { globalApi, listenFirebase, getFirebase, updateFirebase, setFirebase } from 'modules/App/globalApi'
import { createUID } from 'common/utils/createUID'
import {
  buildNewPostWrites,
  buildNewReplyWrites,
  buildPinPostWrites,
  buildDeletePostWrites,
  buildDeleteReplyWrites,
  buildDeleteReplyOnlyWrites,
  isPermissionDeniedError,
  buildLikeOrUnlikePostWrites,
  buildLikeOrUnlikeReplyWrites,
  getFeedPath,
  getCommunityPath,
} from './communityUtils'
import { MAIN_FEED_ID, DASHBOARD_POSTS_PAGE_SIZE, PIN_DURATION_MS } from './communityConstants'
import { GROUP_CALL_TYPE } from './postButtonUtils'

const COMMUNITY_ENABLED_PATH = (coachOrgId) => `coachOrgs/${coachOrgId}/mobilePreMountSettings/community/enabled`

// Keeps an RTK Query cache entry live from a Firebase query. globalApi's
// listenFirebase takes a plain path with no query constraints, so the bounded
// feed listeners (newest N, per-author, needs-reply) build their own query and
// hand it here.
const listenFirebaseQuery = async (firebaseQuery, api, label) => {
  const { updateCachedData, cacheDataLoaded, cacheEntryRemoved } = api
  try {
    await cacheDataLoaded
    onValue(firebaseQuery, (snapshot) => {
      const response = snapshot.val()
      updateCachedData(() => {
        return response || {}
      })
    })
  } catch (e) {
    console.error(`${label} error`, e)
  }
  await cacheEntryRemoved
  off(firebaseQuery)
}

export const communityApi = globalApi.injectEndpoints({
  endpoints: (build) => ({
    listenCommunityEnabled: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        await listenFirebase(COMMUNITY_ENABLED_PATH(coachOrgId), api)
      },
    }),
    setCommunityEnabled: build.mutation({
      queryFn: async ({ coachOrgId, enabled }) => {
        const { data, error } = await setFirebase(COMMUNITY_ENABLED_PATH(coachOrgId), enabled)
        return { data, error }
      },
    }),
    // The feed can grow without bound, so listen to only the newest posts.
    listenCommunityPosts: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        const postsRef = ref(db, `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts`)
        const postsQuery = query(postsRef, orderByChild('createdAt'), limitToLast(DASHBOARD_POSTS_PAGE_SIZE))
        await listenFirebaseQuery(postsQuery, api, 'listenCommunityPosts')
      },
    }),
    // The coach's own posts, for the "Your posts" tab. Posts are indexed by
    // authorId in the rules, so this is an exact per-author query that does not
    // depend on the feed window above. Within one authorId Firebase orders by
    // key (push ids are time-ordered), so limitToLast keeps the newest. Same
    // query mobile's "My posts" filter runs.
    listenMyCommunityPosts: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, userId }, api) => {
        const postsRef = ref(db, `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts`)
        const myPostsQuery = query(
          postsRef,
          orderByChild('authorId'),
          equalTo(userId),
          limitToLast(DASHBOARD_POSTS_PAGE_SIZE)
        )
        await listenFirebaseQuery(myPostsQuery, api, 'listenMyCommunityPosts')
      },
    }),
    // Older posts for the feed's "Load more": one live window of `limit` posts
    // at or before the (endAtCreatedAt, endAtKey) cursor — the head listener's
    // oldest post. Firebase has no exclusive endBefore, so the boundary post is
    // included and deduped against the head at render. usePostPager explains
    // how the cursor and limit move.
    listenOlderCommunityPosts: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, endAtCreatedAt, endAtKey, limit }, api) => {
        const postsRef = ref(db, `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts`)
        const olderQuery = query(
          postsRef,
          orderByChild('createdAt'),
          endAt(endAtCreatedAt, endAtKey),
          limitToLast(limit)
        )
        await listenFirebaseQuery(olderQuery, api, 'listenOlderCommunityPosts')
      },
    }),
    // Same for "Your posts". The cursor is a key, not a createdAt: every record
    // in this query shares one authorId, so Firebase orders them by key.
    // startAt(userId) + endAt(userId, key) is the equalTo(userId) range with a
    // top bound, which equalTo itself cannot express.
    listenOlderMyCommunityPosts: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, userId, endAtKey, limit }, api) => {
        const postsRef = ref(db, `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts`)
        const olderQuery = query(
          postsRef,
          orderByChild('authorId'),
          startAt(userId),
          endAt(userId, endAtKey),
          limitToLast(limit)
        )
        await listenFirebaseQuery(olderQuery, api, 'listenOlderMyCommunityPosts')
      },
    }),
    // Members the coach has banned (mobile's Ban action). Their posts and
    // replies stay in the database but are hidden on every surface — the feed
    // filters by this map the same way the app does. Readable by members and
    // admins, writable by admins only.
    listenBannedMembers: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        await listenFirebase(`${getCommunityPath(coachOrgId)}/moderation/bannedMembers`, api)
      },
    }),
    listenMyCommunityProfile: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, userId }, api) => {
        await listenFirebase(`${getCommunityPath(coachOrgId)}/profiles/${userId}`, api)
      },
    }),
    // Used by the app-page button picker: the Intro option only makes sense for
    // orgs that actually have intro pages.
    listenCommunityIntroPages: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        await listenFirebase(`coachOrgs/${coachOrgId}/introPages`, api)
      },
    }),
    createCommunityPost: build.mutation({
      queryFn: async ({
        coachOrgId,
        userId,
        author,
        text,
        notifyMembers,
        pinned,
        repliesOff,
        richFields,
        postId: givenPostId,
      }) => {
        // The form pre-generates the id when an image upload needs it for the file path.
        const postId = givenPostId || createUID()
        const update = buildNewPostWrites({
          coachOrgId,
          feedId: MAIN_FEED_ID,
          postId,
          authorId: userId,
          author,
          text,
          createdAt: serverTimestamp(),
          notifyMembers,
          pinnedUntil: pinned === true ? Date.now() + PIN_DURATION_MS : undefined,
          repliesOff,
        })
        // Dashboard-only extras (imageUrl, tap-through button fields) ride on top of
        // the mirrored builder so the base record stays identical to mobile's.
        if (richFields && Object.keys(richFields).length > 0) {
          const postPath = Object.keys(update)[0]
          update[postPath] = { ...update[postPath], ...richFields }
        }
        // A group call also queues its "starting now" push for the cloud cron,
        // atomically with the post. The cron re-reads the post before sending,
        // so a later deletion cancels the notification.
        if (richFields?.linkType === GROUP_CALL_TYPE && typeof richFields.callStartAt === 'number') {
          update[`${getCommunityPath(coachOrgId)}/groupCallNotifQueue/${MAIN_FEED_ID}/${postId}`] =
            richFields.callStartAt
          update[`orgsWithUpcomingGroupCalls/${coachOrgId}`] = true
        }
        const { data, error } = await updateFirebase(undefined, update)
        return { data: { status: data, postId }, error }
      },
    }),
    // Rules allow the author to update their own post (authorId/createdAt unchanged).
    // Only the changed fields are written; editedAt marks the post as edited.
    // groupCallQueueUpdate keeps the cron's notification queue in sync when the
    // edit touches a group call: { callStartAt } re-queues (rescheduled call),
    // { remove: true } clears it (button changed away from group call).
    editCommunityPost: build.mutation({
      queryFn: async ({ coachOrgId, postId, fields, groupCallQueueUpdate }) => {
        const postPath = `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts/${postId}`
        const update = {}
        Object.entries({ ...fields, editedAt: Date.now() }).forEach(([key, value]) => {
          update[`${postPath}/${key}`] = value === undefined ? null : value
        })
        if (groupCallQueueUpdate) {
          const queuePath = `${getCommunityPath(coachOrgId)}/groupCallNotifQueue/${MAIN_FEED_ID}/${postId}`
          if (groupCallQueueUpdate.remove) {
            update[queuePath] = null
          } else if (typeof groupCallQueueUpdate.callStartAt === 'number') {
            update[queuePath] = groupCallQueueUpdate.callStartAt
            update[`orgsWithUpcomingGroupCalls/${coachOrgId}`] = true
          }
        }
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // Attached only while a thread is expanded (mirrors mobile's per-post listener).
    listenPostReplies: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, postId }, api) => {
        await listenFirebase(`${getFeedPath(coachOrgId, MAIN_FEED_ID)}/replies/${postId}`, api)
      },
    }),
    createCommunityReply: build.mutation({
      queryFn: async ({ coachOrgId, postId, postAuthorId, userId, author, text, richFields }) => {
        const replyId = createUID()
        const update = buildNewReplyWrites({
          coachOrgId,
          feedId: MAIN_FEED_ID,
          postId,
          postAuthorId,
          replyId,
          activityId: createUID(),
          authorId: userId,
          author,
          text,
          createdAt: serverTimestamp(),
          inc: increment,
        })
        if (richFields && Object.keys(richFields).length > 0) {
          const replyPath = `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/replies/${postId}/${replyId}`
          update[replyPath] = { ...update[replyPath], ...richFields }
        }
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // Rules allow the author to update their own reply (authorId/createdAt unchanged),
    // as for posts. Only the given fields are written, null deleting a field, and
    // editedAt marks the reply as edited.
    editCommunityReply: build.mutation({
      queryFn: async ({ coachOrgId, postId, replyId, fields }) => {
        const replyPath = `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/replies/${postId}/${replyId}`
        const update = {}
        Object.entries({ ...fields, editedAt: Date.now() }).forEach(([key, value]) => {
          update[`${replyPath}/${key}`] = value === undefined ? null : value
        })
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // The coach's own likes (powers the filled-heart state on posts and replies).
    listenMyCommunityLikes: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId, userId }, api) => {
        await listenFirebase(`${getFeedPath(coachOrgId, MAIN_FEED_ID)}/userLikes/${userId}`, api)
      },
    }),
    toggleCommunityPostLike: build.mutation({
      queryFn: async ({ coachOrgId, postId, postAuthorId, userId, actorName, isLiked }) => {
        const update = buildLikeOrUnlikePostWrites({
          coachOrgId,
          feedId: MAIN_FEED_ID,
          postId,
          postAuthorId,
          activityId: createUID(),
          userId,
          actorName,
          isLiked,
          createdAt: Date.now(),
          inc: increment,
        })
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    toggleCommunityReplyLike: build.mutation({
      queryFn: async ({ coachOrgId, postId, replyId, replyAuthorId, userId, actorName, isLiked }) => {
        const update = buildLikeOrUnlikeReplyWrites({
          coachOrgId,
          feedId: MAIN_FEED_ID,
          postId,
          replyId,
          replyAuthorId,
          activityId: createUID(),
          userId,
          actorName,
          isLiked,
          createdAt: Date.now(),
          inc: increment,
        })
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // The coach's needs-reply queue. needsReplySince is server-maintained (set
    // by member posts/replies, cleared by coach replies), so "field exists" IS
    // the queue — this query finds it at any depth, independent of the 100-post
    // feed window.
    listenNeedsReplyPosts: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        const postsRef = ref(db, `${getFeedPath(coachOrgId, MAIN_FEED_ID)}/posts`)
        const needsReplyQuery = query(postsRef, orderByChild('needsReplySince'), startAt(1), limitToLast(500))
        await listenFirebaseQuery(needsReplyQuery, api, 'listenNeedsReplyPosts')
      },
    }),
    // "Mark as done" without replying — clears the queue flag (admin-only rule)
    // and refreshes the coach-activity stamp (the "New" reply highlight anchor).
    // Accepts one id or many: bulk clears are the same multi-path write.
    markPostsDone: build.mutation({
      queryFn: async ({ coachOrgId, postIds }) => {
        const feedPath = getFeedPath(coachOrgId, MAIN_FEED_ID)
        const now = Date.now()
        const update = {}
        postIds.forEach((postId) => {
          update[`${feedPath}/posts/${postId}/needsReplySince`] = null
          update[`${feedPath}/posts/${postId}/lastCoachActivityAt`] = now
        })
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    pinCommunityPost: build.mutation({
      queryFn: async ({ coachOrgId, postId, pinnedUntil }) => {
        const update = buildPinPostWrites({ coachOrgId, feedId: MAIN_FEED_ID, postId, pinnedUntil })
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // ---- Moderation (dashboard-only surface; data model shared with mobile) ----
    // The whole reports node: small (reports are rare), so a plain listener.
    listenCommunityReports: build.query({
      queryFn: () => {
        return { data: { isLoading: true } }
      },
      onCacheEntryAdded: async ({ coachOrgId }, api) => {
        await listenFirebase(`${getFeedPath(coachOrgId, MAIN_FEED_ID)}/reports`, api)
      },
    }),
    // One member's community profile, for showing names on report/ban rows.
    // A query (not a listener): names on a moderation row don't need to be live.
    fetchCommunityMemberProfile: build.query({
      queryFn: async ({ coachOrgId, userId }) => {
        const { data, error } = await getFirebase(`${getCommunityPath(coachOrgId)}/profiles/${userId}`)
        return { data: data || null, error }
      },
    }),
    // Several members' community profiles in one request, keyed by userId, with
    // null for a member who has no profile. The banned list needs every name up
    // front so it can sort and search them, which one request per row cannot do.
    // A read that fails also resolves to null, so one bad row cannot hide the rest.
    fetchCommunityMemberProfiles: build.query({
      queryFn: async ({ coachOrgId, userIds }) => {
        const results = await Promise.all(
          userIds.map((userId) => getFirebase(`${getCommunityPath(coachOrgId)}/profiles/${userId}`))
        )
        return { data: Object.fromEntries(userIds.map((userId, i) => [userId, results[i].data || null])) }
      },
    }),
    // Whether a user is on the org's admin list (`coachOrgs/<org>/admins`), which
    // is what makes them one of the org's coaches. The moderation page uses it so
    // a coach is never offered a ban. A one-time read on purpose: a refused read
    // comes back as an error and resolves to false, where a listener would leave
    // the row waiting forever.
    fetchIsOrgAdmin: build.query({
      queryFn: async ({ coachOrgId, userId }) => {
        const { data } = await getFirebase(`coachOrgs/${coachOrgId}/admins/${userId}`)
        return { data: Boolean(data) }
      },
    }),
    // The reported post or reply, for the content preview. The feed listener
    // only holds the newest posts, so a reported item can sit outside it.
    // null data = the content was already deleted (the row says so).
    fetchReportedTarget: build.query({
      queryFn: async ({ coachOrgId, targetType, postId, replyId }) => {
        const feedPath = getFeedPath(coachOrgId, MAIN_FEED_ID)
        const path = targetType === 'reply' ? `${feedPath}/replies/${postId}/${replyId}` : `${feedPath}/posts/${postId}`
        const { data, error } = await getFirebase(path)
        return { data: data || null, error }
      },
    }),
    // Dismissed = no action needed; resolved = content deleted or author
    // banned. Either way the report record stays as the audit trail.
    setReportStatus: build.mutation({
      queryFn: async ({ coachOrgId, reportId, status }) => {
        const { data, error } = await updateFirebase(undefined, {
          [`${getFeedPath(coachOrgId, MAIN_FEED_ID)}/reports/${reportId}/status`]: status,
        })
        return { data, error }
      },
    }),
    // true bans, null unbans — the exact write mobile's buildBanWrites makes.
    // A banned member's posts are hidden in the app immediately; unban restores.
    setMemberBanned: build.mutation({
      queryFn: async ({ coachOrgId, userId, isBanned }) => {
        const { data, error } = await updateFirebase(undefined, {
          [`${getCommunityPath(coachOrgId)}/moderation/bannedMembers/${userId}`]: isBanned ? true : null,
        })
        return { data, error }
      },
    }),
    // Every member's email keyed by userId: the mirror the app writes at sign-in
    // so a coach can reach a member outside the community. One-shot, fetched the
    // first time a coach opens a member's card.
    fetchCommunityMemberEmails: build.query({
      queryFn: async ({ coachOrgId }) => {
        const { data, error } = await getFirebase(`${getCommunityPath(coachOrgId)}/memberEmails`)
        return { data: data || {}, error }
      },
    }),
    deleteCommunityPost: build.mutation({
      queryFn: async ({ coachOrgId, postId }) => {
        const update = buildDeletePostWrites({ coachOrgId, feedId: MAIN_FEED_ID, postId })
        // Clear any queued "starting now" push (harmless no-op for non-calls).
        // The cron would also catch this by re-reading the deleted post — this
        // just tidies the queue immediately.
        update[`${getCommunityPath(coachOrgId)}/groupCallNotifQueue/${MAIN_FEED_ID}/${postId}`] = null
        const { data, error } = await updateFirebase(undefined, update)
        return { data, error }
      },
    }),
    // Mirrors mobile's buildDeleteReplyWrites: remove the reply, decrement the
    // post's replyCount (rules validate the -1 step).
    deleteCommunityReply: build.mutation({
      queryFn: async ({ coachOrgId, postId, replyId }) => {
        const ids = { coachOrgId, feedId: MAIN_FEED_ID, postId, replyId }
        const result = await updateFirebase(undefined, buildDeleteReplyWrites({ ...ids, inc: increment }))
        // The rules floor replyCount at zero and reject the whole update if it would go
        // below. A count already out of sync at zero would make the reply undeletable, so
        // retry with just the removal. Someone not allowed to delete fails this one too.
        if (isPermissionDeniedError(result.error)) {
          const { data, error } = await updateFirebase(undefined, buildDeleteReplyOnlyWrites(ids))
          return { data, error }
        }
        return { data: result.data, error: result.error }
      },
    }),
  }),
  overrideExisting: false,
})

export const {
  useListenCommunityEnabledQuery,
  useSetCommunityEnabledMutation,
  useListenCommunityPostsQuery,
  useListenMyCommunityPostsQuery,
  useListenOlderCommunityPostsQuery,
  useListenOlderMyCommunityPostsQuery,
  useListenBannedMembersQuery,
  useListenMyCommunityProfileQuery,
  useListenCommunityIntroPagesQuery,
  useCreateCommunityPostMutation,
  useEditCommunityPostMutation,
  usePinCommunityPostMutation,
  useDeleteCommunityPostMutation,
  useDeleteCommunityReplyMutation,
  useListenPostRepliesQuery,
  useCreateCommunityReplyMutation,
  useEditCommunityReplyMutation,
  useListenMyCommunityLikesQuery,
  useToggleCommunityPostLikeMutation,
  useToggleCommunityReplyLikeMutation,
  useMarkPostsDoneMutation,
  useListenNeedsReplyPostsQuery,
  useListenCommunityReportsQuery,
  useFetchCommunityMemberProfileQuery,
  useFetchCommunityMemberProfilesQuery,
  useFetchReportedTargetQuery,
  useFetchIsOrgAdminQuery,
  useSetReportStatusMutation,
  useSetMemberBannedMutation,
  useFetchCommunityMemberEmailsQuery,
} = communityApi
